import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowRight, Loader2 } from "lucide-react";
import {
  authRequestOtp,
  authVerifyOtp,
  requestUpgrade,
} from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";

/**
 * LoginModal — two-step OTP flow.
 *   Step "email":  user enters email → POST /auth/request-otp
 *   Step "code":   user enters 6-digit code → POST /auth/verify-otp → JWT cookie set
 *
 * Controlled component: `open` + `onOpenChange` wire it to whatever trigger
 * sits in the header.
 */
export function LoginModal({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("email");
    setEmail("");
    setCode("");
    setBusy(false);
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await authRequestOtp(email.trim());
      toast.success("Code sent", {
        description: "Check your inbox — it expires in 5 minutes.",
      });
      setStep("code");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send login code.",
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    const trimmed = (code || "").replace(/\s+/g, "");
    if (!/^\d{6}$/.test(trimmed)) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    try {
      const { user } = await authVerifyOtp(email.trim(), trimmed);
      toast.success(`Welcome, ${user.email}`, {
        description: `Signed in on the ${user.plan} plan.`,
      });
      await refresh();
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-w-sm"
        data-testid="login-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-[#002FA7]">
            {step === "email" ? "Sign in with email" : "Enter your code"}
          </DialogTitle>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={requestCode} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-xs uppercase tracking-wider">
                Work email
              </Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="login-email"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#002FA7] hover:bg-[#002072] text-white h-10 text-sm font-semibold"
              data-testid="login-send-otp-btn"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Email me a code <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </>
              )}
            </Button>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              No password needed. We'll email a 6-digit code that expires in 5 min.
              Demo mode works without login — trial unlocks after sign-in.
            </p>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="login-code" className="text-xs uppercase tracking-wider">
                6-digit code
              </Label>
              <div className="relative">
                <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="login-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="pl-9 tracking-[0.4em] font-mono text-center text-lg"
                  data-testid="login-otp-input"
                />
              </div>
              <div className="text-[11px] text-slate-500">
                Sent to <span className="font-semibold">{email}</span>.{" "}
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                  }}
                  className="underline hover:text-[#002FA7]"
                  data-testid="login-change-email"
                >
                  Change email
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#002FA7] hover:bg-[#002072] text-white h-10 text-sm font-semibold"
              data-testid="login-verify-otp-btn"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * HeaderAuthBadge — tiny pill in the top-right showing plan + auth actions.
 *  - Anonymous (demo): "Demo · Login" button
 *  - Trial/Paid: email + plan pill + Upgrade (trial only) + Logout menu
 */
export function HeaderAuthBadge() {
  const { user, loading, logout } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [upgradeBusy, setUpgradeBusy] = useState(false);

  if (loading || !user) return null;

  const isDemo = user.is_demo || user.plan === "demo";

  const onUpgradeClick = async () => {
    setUpgradeBusy(true);
    try {
      const r = await requestUpgrade("dashboard_upgrade_cta");
      toast.success("Upgrade requested", { description: r.message });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request.");
    } finally {
      setUpgradeBusy(false);
    }
  };

  return (
    <>
      {isDemo ? (
        <div className="flex items-center gap-2" data-testid="auth-badge-demo">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200">
            Demo mode
          </span>
          <Button
            size="sm"
            onClick={() => setLoginOpen(true)}
            className="h-7 rounded-full bg-[#002FA7] hover:bg-[#002072] text-white px-3 text-xs font-semibold"
            data-testid="header-login-btn"
          >
            Login
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2" data-testid="auth-badge-authed">
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
              user.plan === "paid"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
            data-testid="header-plan-pill"
          >
            {user.plan}
          </span>
          <span className="text-[12px] text-slate-600 max-w-[200px] truncate">
            {user.email}
          </span>
          {user.plan === "trial" && (
            <Button
              size="sm"
              onClick={onUpgradeClick}
              disabled={upgradeBusy}
              className="h-7 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white px-3 text-xs font-semibold"
              data-testid="header-upgrade-btn"
            >
              Upgrade
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={logout}
            className="h-7 rounded-full px-3 text-xs font-semibold"
            data-testid="header-logout-btn"
          >
            Logout
          </Button>
        </div>
      )}
      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
    </>
  );
}
